import type { CSSObject } from '@emotion/react'
import { Constants } from './styleConstants'

/**
 * Every section declares a container under this name, so section rules read
 * the section's own width rather than the page's. The same rules then hold
 * wherever a section renders — the page, a drawer, or nested inside another
 * section — with no separate cases.
 */
export const SECTION_CONTAINER_NAME = 'section'

export const sectionMedia = (minWidth: number) =>
    `@container ${SECTION_CONTAINER_NAME} (min-width: ${minWidth}px)`

/**
 * Viewport width at which the storefront switches to its desktop layout.
 *
 * Exported to admin as well, because the layout reads the viewport rather than
 * a container: an iframe meant to show a phone has to be narrower than this or
 * the desktop layout renders inside it.
 */
export const STOREFRONT_DESKTOP_BREAKPOINT_PX = Constants.EXPANDED_BREAKPOINT

/** Layout constants for the scrolling storefront [sc-36106]. */
export const ScrollingLayout = {
    /**
     * Chrome uses a viewport media query rather than a container query. The
     * page is the viewport — nothing declares `container-type` — which is also
     * why `position: fixed` behaves normally.
     */
    DESKTOP_MEDIA: `@media (min-width: ${STOREFRONT_DESKTOP_BREAKPOINT_PX}px)`,

    /** Content stops growing here and centres, leaving symmetric gutters. */
    CONTENT_MAX_WIDTH: 1200,

    /**
     * The main CTA keeps roughly the width it has today rather than stretching
     * to the content cap — a button spanning 1100px reads as a banner.
     */
    CTA_MAX_WIDTH: 480,

    /**
     * Sections stay capped at the legacy column width until each one is
     * migrated to read its own container, so an unmigrated section is always
     * rendered at a width it was designed for.
     */
    UNMIGRATED_SECTION_MAX_WIDTH: Constants.CONTENT_BLOCK_DESKTOP_WIDTH,

    PAGE_GUTTER_PX: Constants.CONTENT_BLOCK_MOBILE_MARGIN,
    PAGE_GUTTER_DESKTOP_PX: 48,

    /**
     * Prose measure. Body copy stops here however wide the section gets —
     * a 1200px line is unreadable regardless of how much room there is.
     */
    PROSE_MAX_WIDTH: 720,

    /**
     * The cart opens as a panel beside the page rather than covering it, so
     * the shopper keeps sight of what they were looking at.
     */
    CART_PANEL_WIDTH: 480,

    /** Breathing room between the sticky header and the pinned hero video. */
    HERO_STICKY_GAP_PX: 16,
}

/**
 * Height of the sticky header, published by the shell so the pinned hero video
 * can offset itself below it. The header's height varies with whether the
 * storefront has a banner, so it is measured rather than assumed.
 */
export const HEADER_HEIGHT_VAR = '--sf-header-height'

/**
 * Multiplies every type size our components set. Unset below the desktop
 * breakpoint, where the fallback of 1 leaves sizes exactly as authored.
 */
export const FONT_SCALE_VAR = '--sf-font-scale'

/**
 * Type designed for a phone reads small on a desktop monitor, which is further
 * away. One factor over the whole scale keeps the existing relationships
 * between sizes intact — there is nothing per-role to tune or keep in step.
 */
export const DESKTOP_FONT_SCALE = 1.125

/**
 * Wrap a px type size so it follows the layout's scale. Sizes derived by
 * measurement rather than authored — the CTA's fit-to-width, for instance —
 * deliberately stay out of this, since they are already sized to their box.
 */
export const scaledFontSize = (px: number): string =>
    `calc(${px}px * var(${FONT_SCALE_VAR}, 1))`

/**
 * Width of the page's content area, published by the shell so a section can
 * bleed its background to the page edges.
 *
 * Measured rather than using `100vw`, which counts the scrollbar and so
 * overflows by its width.
 */
export const PAGE_WIDTH_VAR = '--sf-page-width'

/**
 * The gutter a section puts between its own content and its edge. An ancestor
 * that already insets the whole tree — a product laying its columns out on one
 * measure — sets this to zero so sections inside it stop adding a second one.
 */
export const SECTION_GUTTER_VAR = '--sf-section-gutter'

export const sectionGutter = `var(${SECTION_GUTTER_VAR}, ${Constants.CONTENT_BLOCK_MOBILE_MARGIN}px)`

/**
 * Spans a section's background across the page while its content stays put.
 * Costs nothing when the variable is unset.
 */
export const pageBleed = {
    width: `var(${PAGE_WIDTH_VAR}, 100%)`,
    marginInline: `calc((100% - var(${PAGE_WIDTH_VAR}, 100%)) / 2)`,
}

/**
 * Scopes rules to a section container. Required because `cqw` silently falls
 * back to the viewport where no container is declared, which a section
 * rendered outside the page tree would otherwise size against.
 */
export const inScrollingLayout = (styles: CSSObject): CSSObject => ({
    [sectionMedia(0)]: styles,
})

/** For rules that apply to a section only once the window is desktop-wide. */
export const inScrollingDesktop = (styles: CSSObject): CSSObject =>
    inScrollingLayout({ [ScrollingLayout.DESKTOP_MEDIA]: styles })

const TRACK_CONTENT_INSET = `calc((var(${PAGE_WIDTH_VAR}, 100%) - 100cqw) / 2 + ${sectionGutter})`

/**
 * Raw styles for the page-bleed track, unwrapped from `inScrollingLayout` so
 * callers that need to add their own rules to the same container query can
 * compose them and wrap once. Spreading `pageBleedTrack` and another
 * `inScrollingLayout(...)` in the same object literal would collide on the
 * container-query key and drop everything from the first spread.
 */
export const pageBleedTrackStyles: CSSObject = {
    ...pageBleed,
    paddingInline: TRACK_CONTENT_INSET,
    scrollPaddingInline: TRACK_CONTENT_INSET,
}

/** Horizontal track aligned to the content edge but scrollable to page edges. */
export const pageBleedTrack = inScrollingLayout(pageBleedTrackStyles)

/**
 * Steps for section internals, in terms of the section's own width. Sized so
 * each is reachable inside the content column (CONTENT_MAX_WIDTH less the
 * page gutters). Desktop-ready HTML can be wider than that column — it still
 * uses these steps, then keeps growing with the page.
 */
export const SectionMedia = {
    /** Comfortably two-up. */
    MD: sectionMedia(540),
    /** Comfortably three-up. */
    LG: sectionMedia(800),
    /** Comfortably four-up. */
    XL: sectionMedia(1040),
}
